import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneOutgoing, PhoneCall, CheckCircle2, Flame, Users, TrendingUp,
  ArrowRight, ArrowUpDown, Calendar,
} from 'lucide-react';
import { useOutbound } from '../../../lib/outboundStore';
import { cn } from '../../../lib/utils';

/* ------------------------------------------------------------------
   Admin Reports → Outbound Tab
   Answers the three questions an admin actually asks about the dialer:
     • Who's on the phones?           → Calls per LO
     • Are they reaching anyone?      → Connect rate
     • Is the queue producing loans?  → Promoted-to-CRM rate

   Data comes from the shared OutboundStore (already hydrates the
   Outbound page for admins with the full firm-wide cache incl. the
   promoted-hot leads whose `outbound` flag has been cleared).
   ------------------------------------------------------------------ */

const CONNECT_STATUSES = new Set(['contacted', 'follow_up', 'appointment', 'hot']);
const CONNECT_RESULTS = new Set(['connected']);

// Statuses that mean the LO had a live conversation. `not_interested`
// is included here because the borrower had to be reached for the LO
// to know they weren't interested.
const REACHED_STATUSES = new Set([...CONNECT_STATUSES, 'not_interested']);

function pct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 1000) / 10;
}

export default function OutboundReportsTab({ range, filters, team }) {
  const outboundLeads = useOutbound();
  const [sort, setSort] = useState({ key: 'attempts', dir: 'desc' });

  // In-range slice — outbound leads pivot off `created_at` (import date).
  // If a lead was imported before the range but had a call attempt
  // inside the range, we still want to count that call. Since we don't
  // have per-call timestamps client-side, we approximate: keep leads
  // whose `created_at` is in-range OR whose `outboundLastCallAt` is
  // in-range. That covers "hustle over old leads" campaigns cleanly.
  const inRange = useMemo(() => {
    if (!range) return outboundLeads;
    const start = range.start.getTime();
    const end = range.end.getTime() + 24 * 60 * 60 * 1000 - 1;
    return outboundLeads.filter((l) => {
      const c = l.created_at ? new Date(l.created_at).getTime() : 0;
      const lc = l.outboundLastCallAt ? new Date(l.outboundLastCallAt).getTime() : 0;
      return (c >= start && c <= end) || (lc >= start && lc <= end);
    });
  }, [outboundLeads, range]);

  // Apply the shared admin LO filter (Reports header). If unset, all LOs.
  const scoped = useMemo(() => {
    const loIds = filters?.loIds;
    if (!loIds || loIds.size === 0) return inRange;
    return inRange.filter((l) => loIds.has(l.assignedTo || 'unassigned'));
  }, [inRange, filters]);

  // Aggregate per LO.
  const rows = useMemo(() => {
    const map = new Map();
    const seed = (id) => {
      if (!map.has(id)) {
        map.set(id, {
          id,
          leads: 0,        // total outbound rows assigned
          attempts: 0,     // sum of outboundAttempts (proxy for total dials)
          connects: 0,     // leads reached (live conversation)
          appointments: 0, // leads booked
          promoted: 0,     // leads that became hot / went to CRM
          notInterested: 0,
        });
      }
      return map.get(id);
    };

    scoped.forEach((l) => {
      const row = seed(l.assignedTo || 'unassigned');
      row.leads += 1;
      row.attempts += Number(l.outboundAttempts) || 0;
      const status = l.outboundStatus || 'new';
      if (REACHED_STATUSES.has(status) || CONNECT_RESULTS.has(l.outboundLastCallResult || '')) {
        row.connects += 1;
      }
      if (status === 'appointment') row.appointments += 1;
      if (status === 'hot') row.promoted += 1;
      if (status === 'not_interested') row.notInterested += 1;
    });

    return Array.from(map.values()).map((row) => {
      const member = team?.find((t) => t.id === row.id);
      return {
        ...row,
        name: member?.name || (row.id === 'unassigned' ? 'Unassigned' : 'LO'),
        title: member?.title || (member?.role === 'admin' ? 'Administrator' : 'Broker'),
        connectRate: pct(row.connects, row.leads),
        promotionRate: pct(row.promoted, row.leads),
      };
    });
  }, [scoped, team]);

  const totals = useMemo(() => {
    const t = rows.reduce(
      (acc, r) => ({
        leads: acc.leads + r.leads,
        attempts: acc.attempts + r.attempts,
        connects: acc.connects + r.connects,
        appointments: acc.appointments + r.appointments,
        promoted: acc.promoted + r.promoted,
      }),
      { leads: 0, attempts: 0, connects: 0, appointments: 0, promoted: 0 },
    );
    return {
      ...t,
      connectRate: pct(t.connects, t.leads),
      promotionRate: pct(t.promoted, t.leads),
      callsPerLead: t.leads ? Math.round((t.attempts / t.leads) * 10) / 10 : 0,
    };
  }, [rows]);

  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (sort.key === 'name') {
        return sort.dir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sort.dir === 'asc' ? (av - bv) : (bv - av);
    });
    return list;
  }, [rows, sort]);

  const maxAttempts = Math.max(1, ...rows.map((r) => r.attempts));
  const maxPromoted = Math.max(1, ...rows.map((r) => r.promoted));

  const empty = outboundLeads.length === 0;

  return (
    <div className="space-y-4">
      {/* KPI strip — the three questions the tab exists to answer + context. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="outbound-reports-kpis">
        <KpiTile
          label="Outbound Leads"
          value={totals.leads.toLocaleString()}
          icon={Users}
          hint={`${rows.length} LO${rows.length === 1 ? '' : 's'} on the queue`}
        />
        <KpiTile
          label="Call Attempts"
          value={totals.attempts.toLocaleString()}
          icon={PhoneCall}
          hint={`${totals.callsPerLead} avg / lead`}
          tone="gold"
        />
        <KpiTile
          label="Connect Rate"
          value={`${totals.connectRate}%`}
          icon={CheckCircle2}
          hint={`${totals.connects.toLocaleString()} reached`}
          tone="sky"
        />
        <KpiTile
          label="Promoted to CRM"
          value={totals.promoted.toLocaleString()}
          icon={Flame}
          hint={`${totals.appointments.toLocaleString()} appts`}
          tone="hot"
        />
        <KpiTile
          label="Promotion Rate"
          value={`${totals.promotionRate}%`}
          icon={TrendingUp}
          hint={`hot / outbound leads`}
          tone="violet"
        />
      </div>

      {/* Per-LO breakdown */}
      <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="outbound-reports-table">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b hairline">
          <div className="flex items-center gap-2">
            <PhoneOutgoing className="h-4 w-4 text-neutral-500" />
            <h2 className="text-[13px] font-bold tracking-tight">Per Loan Officer</h2>
            <span className="text-[11px] text-neutral-500 tabular-nums">
              · {rows.length} on the dialer
            </span>
          </div>
          <Link
            to="/outbound"
            data-testid="outbound-reports-open-workspace"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-700 hover:text-neutral-900 transition"
          >
            Open workspace <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {empty ? (
          <EmptyState />
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-[12.5px] text-neutral-500">
            No outbound activity in the selected range.
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 gap-x-3 px-5 py-2 text-[10.5px] uppercase tracking-[0.14em] text-neutral-500 border-b hairline">
              <SortHeader label="Loan Officer" col="name"        sort={sort} onSort={setSort} className="col-span-2 text-left" />
              <SortHeader label="Leads"        col="leads"       sort={sort} onSort={setSort} className="col-span-1 text-center" />
              {/* Empty bar column — no header, keeps the heat bar visually
                  labelled by the value column immediately to its right. */}
              <div className="col-span-1" aria-hidden />
              <SortHeader label="Attempts"     col="attempts"    sort={sort} onSort={setSort} className="col-span-1 text-center" />
              <SortHeader label="Connects"     col="connects"    sort={sort} onSort={setSort} className="col-span-1 text-center" />
              <div className="col-span-1" aria-hidden />
              <SortHeader label="Connect %"    col="connectRate" sort={sort} onSort={setSort} className="col-span-2 text-center" />
              <SortHeader label="Promoted"     col="promoted"    sort={sort} onSort={setSort} className="col-span-1 text-center" />
              <div className="col-span-1" aria-hidden />
              <SortHeader label="Promotion %"  col="promotionRate" sort={sort} onSort={setSort} className="col-span-1 text-center" />
            </div>

            <div className="divide-y hairline">
              {sorted.map((r) => (
                <LoRow
                  key={r.id}
                  row={r}
                  maxAttempts={maxAttempts}
                  maxPromoted={maxPromoted}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <p className="text-[11px] text-neutral-500 leading-relaxed">
        <b className="text-neutral-700">Connects</b> = leads that reached a live conversation
        (status <code>contacted</code>, <code>follow_up</code>, <code>appointment</code>, <code>hot</code>,
        or a last-call result of <code>connected</code>).{' '}
        <b className="text-neutral-700">Promoted to CRM</b> counts leads whose LO flagged
        them <code>Hot</code>, which automatically slides the record into the primary
        pipeline. Range filter pivots on the outbound import date or the last-call date.
      </p>
    </div>
  );
}

/* ── Row ────────────────────────────────────────────────────────── */
function LoRow({ row, maxAttempts, maxPromoted }) {
  const initials = row.name
    .split(' ')
    .filter(Boolean)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      data-testid={`outbound-reports-lo-${row.id}`}
      className="grid grid-cols-12 gap-x-3 px-5 py-3 items-center hover:bg-neutral-50 transition-colors"
    >
      {/* Name */}
      <div className="col-span-2 flex items-center gap-2.5 min-w-0">
        <div className="h-7 w-7 shrink-0 rounded-full bg-neutral-900 text-white text-[10px] font-bold flex items-center justify-center">
          {initials || '?'}
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-neutral-900 truncate leading-tight">{row.name}</div>
          <div className="text-[10.5px] text-neutral-500 truncate">{row.title}</div>
        </div>
      </div>

      {/* Leads */}
      <div className="col-span-1 text-center text-[13px] font-bold tabular-nums text-neutral-900">
        {row.leads.toLocaleString()}
      </div>

      {/* Attempts — bar in its own cell, value in its own cell centered
          directly under the ATTEMPTS header. */}
      <div className="col-span-1 flex items-center">
        <HeatBar value={row.attempts} max={maxAttempts} tone="gold" />
      </div>
      <div className="col-span-1 text-center text-[13px] font-bold tabular-nums text-neutral-900">
        {row.attempts.toLocaleString()}
      </div>

      {/* Connects */}
      <div className="col-span-1 text-center text-[13px] font-semibold tabular-nums text-neutral-700">
        {row.connects.toLocaleString()}
      </div>

      {/* Connect rate — bar cell + value cell */}
      <div className="col-span-1 flex items-center">
        <MiniBar value={row.connectRate} max={100} tone="sky" />
      </div>
      <div className="col-span-2 text-center text-[13px] font-bold tabular-nums text-neutral-900">
        {row.connectRate}%
      </div>

      {/* Promoted */}
      <div className="col-span-1 flex items-center justify-center gap-1.5">
        {row.promoted > 0 && <Flame className="h-3.5 w-3.5 text-red-500" strokeWidth={2.4} />}
        <span className="text-[13px] font-semibold tabular-nums text-neutral-700">
          {row.promoted.toLocaleString()}
        </span>
      </div>

      {/* Promotion rate — bar cell + value cell */}
      <div className="col-span-1 flex items-center">
        <MiniBar value={row.promotionRate} max={Math.max(20, Math.min(100, maxPromoted * 2))} tone="hot" />
      </div>
      <div className="col-span-1 text-center text-[13px] font-bold tabular-nums text-neutral-900">
        {row.promotionRate}%
      </div>
    </div>
  );
}

function HeatBar({ value, max, tone = 'gold' }) {
  const w = Math.min(100, (value / max) * 100);
  const bg =
    tone === 'gold' ? 'bg-[hsl(var(--kf-gold))]' :
    tone === 'sky' ? 'bg-sky-500' :
    tone === 'hot' ? 'bg-red-500' :
    'bg-neutral-800';
  return (
    <div className="w-full h-1.5 rounded-full bg-neutral-100 overflow-hidden">
      <div className={cn('h-full', bg)} style={{ width: `${w}%` }} />
    </div>
  );
}

function MiniBar({ value, max, tone }) {
  const w = Math.min(100, (value / max) * 100);
  const bg =
    tone === 'sky' ? 'bg-sky-500' :
    tone === 'hot' ? 'bg-red-500' :
    tone === 'gold' ? 'bg-[hsl(var(--kf-gold))]' :
    'bg-neutral-800';
  return (
    <div className="w-full h-1.5 rounded-full bg-neutral-100 overflow-hidden">
      <div className={cn('h-full', bg)} style={{ width: `${w}%` }} />
    </div>
  );
}

function SortHeader({ label, col, sort, onSort, className }) {
  const active = sort.key === col;
  const alignRight = className?.includes('text-right');
  const alignCenter = className?.includes('text-center');
  const justify = alignRight ? 'justify-end' : alignCenter ? 'justify-center' : 'justify-start';
  return (
    <div className={cn('flex items-center', justify, className)}>
      <button
        type="button"
        data-testid={`outbound-reports-sort-${col}`}
        onClick={() =>
          onSort((s) =>
            s.key === col
              ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
              : { key: col, dir: col === 'name' ? 'asc' : 'desc' },
          )
        }
        className={cn(
          'inline-flex items-center gap-1 font-semibold uppercase tracking-[0.14em] hover:text-neutral-900 transition',
          active ? 'text-neutral-900' : 'text-neutral-500',
        )}
      >
        {alignRight ? (
          <>
            <ArrowUpDown className={cn('h-3 w-3', active ? 'opacity-100' : 'opacity-40')} />
            <span>{label}</span>
          </>
        ) : (
          <>
            <span>{label}</span>
            <ArrowUpDown className={cn('h-3 w-3', active ? 'opacity-100' : 'opacity-40')} />
          </>
        )}
      </button>
    </div>
  );
}

/* ── KPI tile ─────────────────────────────────────────────────────── */
function KpiTile({ label, value, icon: Icon, hint, tone = 'default' }) {
  const accent =
    tone === 'gold' ? 'text-[hsl(var(--kf-gold))]' :
    tone === 'sky' ? 'text-sky-500' :
    tone === 'hot' ? 'text-red-500' :
    tone === 'violet' ? 'text-violet-500' :
    'text-neutral-500';
  return (
    <div className="rounded-2xl border hairline bg-white px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-neutral-500">
        <Icon className={cn('h-3.5 w-3.5', accent)} strokeWidth={2.4} />
        <span>{label}</span>
      </div>
      <div className="mt-1.5 text-[26px] font-black tracking-tight text-neutral-900 tabular-nums leading-none">
        {value}
      </div>
      {hint && (
        <div className="mt-1 text-[10.5px] text-neutral-500 tabular-nums truncate">{hint}</div>
      )}
    </div>
  );
}

/* ── Empty state ─────────────────────────────────────────────────── */
function EmptyState() {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center">
        <PhoneOutgoing className="h-5 w-5 text-neutral-500" />
      </div>
      <div className="mt-3 text-[14px] font-bold text-neutral-900">No outbound activity yet</div>
      <p className="mt-1 text-[12px] text-neutral-500 max-w-md mx-auto">
        Import a CSV of prospects on the Outbound workspace and this tab will
        break out calls, connect rate and promotion rate per loan officer.
      </p>
      <Link
        to="/outbound/import"
        data-testid="outbound-reports-empty-import"
        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-3.5 py-2 text-[12.5px] font-bold text-white hover:brightness-110 transition"
      >
        Import a list <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
